import React, { useState, useRef, useEffect } from "react"
import { graphql } from "gatsby"
import { useGesture } from "react-use-gesture"

import Layout from "../components/layout"
import styles from "./living-brand-studio.module.scss"
import intelligenceIconGif from "../images/living-brand-studio/intelligence-icon.gif"
import sensingIconGif from "../images/living-brand-studio/sensing-icon.gif"
import changeGif from "../images/living-brand-studio/change.gif"

const IntelligenceIcon = () => (
  <img src={intelligenceIconGif} alt="" width="248" height="248" />
)

const SensingIcon = () => (
  <img src={sensingIconGif} alt="" width="248" height="248" />
)

// Hover is bound to the whole heading rather than just the "change" word:
// the gif overlay renders wider than the word's own text, and a mouse
// resting on that overlay sits outside the word's hit area, so a hover
// scoped to the word alone flickers off the instant the gif appears.
// Remounting the gif (via `key`) on every mouseEnter restarts it from its
// first frame each time it's hovered, rather than resuming mid-loop.
const HeroHeading = () => {
  const [isHovering, setIsHovering] = useState(false)
  const [playCount, setPlayCount] = useState(0)

  return (
    <h1
      className={`h-display h--serif ${styles.heroHeading}`}
      onMouseEnter={() => {
        setIsHovering(true)
        setPlayCount(count => count + 1)
      }}
      onMouseLeave={() => setIsHovering(false)}
    >
      A new brand philosophy, made for an age of{" "}
      <span className={styles.heroChangeWrap}>
        <span
          className={`${styles.heroChangeText} ${
            isHovering ? styles.heroChangeTextHidden : ""
          }`}
        >
          {/* The gif itself already ends on a period, so it's included
          here (rather than as static text after the wrap) to hide along
          with the word — otherwise hovering shows two periods. */}
          change.
        </span>
        {isHovering && (
          <img
            key={playCount}
            className={styles.heroChangeGif}
            src={changeGif}
            alt=""
            aria-hidden="true"
          />
        )}
      </span>
    </h1>
  )
}

// TODO(assets): swatches are flat color placeholders until the real icons arrive.
const PROCESS_STEPS = [
  {
    name: "Bootcamp",
    swatch: "#F97F5F",
    body:
      "A focused sprint to map your opportunity, pressure-test use cases, and prototype what a Living Brand could do for your team.",
  },
  {
    name: "Build",
    swatch: "#FAC541",
    body:
      "A deeper engagement to capture brand knowledge and structure the intelligence and sensing layers behind your Living Brand.",
  },
  {
    name: "Connect",
    swatch: "#F8A7FF",
    body:
      "Custom apps and integrations connect the Living Brand system to your most relevant tools, teams, and workflows.",
  },
  {
    name: "Rollout",
    swatch: "#DDF946",
    body:
      "Formalize the Living Brand Studio operating playbook with governance roles, update cadence, and escalation paths.",
  },
]

const CAPABILITIES = [
  {
    heading: "Sharper starts",
    body:
      "Make briefs stronger before the work begins. Clarify audiences, sharpen inputs, pressure-test claims, and push teams toward more useful creative territory.",
  },
  {
    heading: "Creative development",
    body:
      "Help teams get to better work sooner. Generate on-brand drafts, campaign directions, product copy, and creative builds with brand logic already built in.",
  },
  {
    heading: "Active governance",
    body:
      "Catch drift before it becomes a problem. Flag off-brand tone, competitor mimicry, cultural risk, or portfolio overlap while the work is still taking shape.",
  },
  {
    heading: "Smarter adaptation",
    body:
      "Flex with intention. Adjust tone, message, and expression by audience, channel, market, or moment without feeling random or opportunistic.",
  },
  {
    heading: "Decentralized knowledge",
    body:
      "Make brand expertise easier to access and understand. Help new teams, agencies, and partners learn and apply the instinct behind the rules.",
  },
  {
    heading: "Strategic growth",
    body:
      "Evaluate new spaces through a distinct brand lens. Help teams assess new products, categories, audiences, and innovations with logic and nuance.",
  },
]

// Hidden per request — content/component still intact below.
const SHOW_PRACTICE = false

const PRACTICE = [
  {
    tag: "Fortune 50 retailer",
    heading: "Enterprise brand brain infrastructure",
    body:
      "Centralized brand intelligence that plugs into workflows across teams, markets, and partners. Every brief, every asset, every decision starting from the same source of truth, not a guess.",
    callout: "Faster briefs. Fewer revision cycles. Coherence across hundreds of teams.",
  },
  {
    tag: "Global food brand",
    heading: "Living consumer intelligence",
    body:
      "Research and segmentation turned into living, conversational personas teams can work with every day. Insights that stop sitting in decks and start shaping decisions in real time.",
    callout:
      "Strategic intelligence embedded in the creative process, not delivered at the start of a brief cycle.",
  },
  {
    tag: "Nutrition innovation startup",
    heading: "Culture at the speed it changes",
    body:
      "A living trend system that spots shifts early and translates them into ready-to-use creative direction. Teams that stop chasing culture and start leading it.",
    callout: "Proactive cultural intelligence, connected directly to creative output.",
  },
]

const FAQS = [
  {
    question: "How long does it take to build?",
    answer:
      "Most core builds take 8 to 12 weeks. You'll have working tools in your hands well before the end. The exact timeline depends on where your brand is: fully defined, actively evolving, or being created from scratch.",
  },
  {
    question: "How do you do it? What's the process?",
    answer:
      "Four steps. We ingest and scan everything your brand has already written down. We run working sessions to capture what's never been written down, which is usually the most valuable part. We structure all of it into a system AI can reason with. Then we test it with your team until it earns trust.",
  },
  {
    question: "What's in Living Brand Studio?",
    answer:
      "Three layers. The Core: who you are and what never changes. The Memory: your accumulated knowledge, decisions, and judgment. The Senses: live signals from culture, customers, and performance. Together they let AI think like your brand, not just reference your guidelines.",
  },
  {
    question: "How do you update and manage it?",
    answer:
      "Each layer moves at its own speed. The Senses update continuously. The Memory grows every time your brand makes a decision worth keeping. The Core changes rarely, and only deliberately. Nothing enters the Studio without review.",
  },
  {
    question: "What's the process for managing it?",
    answer:
      "We set up a simple stewardship model with named owners on your side and ours, a regular calibration rhythm, and a clear rule for what gets promoted into the Studio versus what stays out. It's governance designed to run in hours per month, not headcount.",
  },
  {
    question: "How does it integrate with our current systems?",
    answer:
      "Through MCP, the open standard for connecting AI to tools and data. Living Brand Studio plugs into the tools your teams already use, including Claude, Slack, Figma, Miro, and your existing platforms. We don't ask you to replace anything.",
  },
  {
    question: "Where is it hosted?",
    answer:
      "During the build, in a Zeus Jones hosted, access-controlled environment that holds only your approved brand and marketing materials and never connects to your internal systems. At delivery, the full system transfers to your environment, governed by your teams. Each client's Studio is fully isolated throughout. No brand's knowledge ever touches another's.",
  },
  {
    question: "What AI platform are you using to build the model?",
    answer:
      "A suite of platforms. The infrastructure runs primarily on DigitalOcean, with AI powered by OpenAI today. The Studio itself is model-agnostic by design, so we can utilize any of the major models, and we'll recommend whichever makes the most sense for your brand. We share the full vendor list, and how each handles data, as part of any review.",
  },
  {
    question: "Are you sharing data back to the larger third-party LLMs?",
    answer:
      "No. Living Brand Studio is a governed knowledge layer the AI reads at the moment of a request. It does not feed, fine-tune, or improve any third-party model. Everything runs through enterprise agreements with contractual no-training commitments. Your knowledge stays yours.",
  },
  {
    question: "Does this meet our data privacy requirements?",
    answer:
      "We design to your requirements, not around them. The Studio uses only brand and marketing materials your team approves. We never need or request sensitive business information: no financial systems, no customer records, no employee data, no trade secrets. We'll complete your security review, NDA, or DPA, and we'd rather start that early. We're currently building for clients in regulated industries.",
  },
  {
    question: "Who owns the model?",
    answer:
      "You do. At delivery, Living Brand Studio and its full knowledge base transfer to your systems as an owned brand asset. It's built on open standards and portable across platforms, so if the AI landscape shifts, it moves with you. After handoff, we can stay on as a governance partner or your team can run it independently. No lock-in, including to us.",
  },
  {
    question: "Who uses Living Brand Studio?",
    answer:
      "Anyone who makes decisions in your brand's name. Brand and marketing teams use it daily. Creative teams and agency partners use it to stay sharp and consistent. Leadership uses it to see brand health in real time. It's simple, purpose-built, and requires no technical skill.",
  },
  {
    question: "How are other clients deploying their Living Brand Studio?",
    answer:
      "A few common patterns: an Ask tool that answers brand questions instantly, a Sensor that scans culture for opportunities worth acting on, creative review tools that check work against the brand before it ships, and dashboards that track brand health over time. Every Studio is built around the moments that matter most to that brand.",
  },
  {
    question:
      "How is this different from the AI tools we already have, like ChatGPT, Claude, or Copilot?",
    answer:
      "Those are engines. Living Brand Studio is what makes them yours. A general model knows about your brand from the public internet. Yours knows your strategy, your judgment, your history, and your rules, and it brings that intelligence into whatever AI tools your teams use. Same engines, radically better output.",
  },
  {
    question: "Will this replace our teams or our agencies?",
    answer:
      "No. It makes them faster and more consistent. The Studio handles the ladder work: retrieval, drafting, checking. Your people keep the leaps: the strategy, the taste, the calls only humans should make.",
  },
  {
    question: "How do we know it's working?",
    answer:
      "You'll feel it in weeks: faster briefs, fewer rounds, more consistent work. We also set baseline measures at kickoff, from adoption and time savings to brand consistency scores, so the value is visible, not anecdotal.",
  },
  {
    question: "How do we get started?",
    answer:
      "One conversation. We'll assess where your brand knowledge stands today, recommend the right entry point, and scope a build with clear phases and deliverables. Most clients start seeing the system take shape within the first month.",
  },
  {
    question: "Does Zeus Jones share Living Brand Studio data across other clients or projects?",
    answer:
      "No. Every client's data is firewalled by project and access-controlled. Nothing we learn about your business ever appears in work for any other client.",
  },
]

// Wraps the active item circularly, e.g. for 6 items and active=0, distances
// come out -2, -1, 0, 1, 2, 3 — matching the carousel wireframe's spread of
// two peeking cards on either side, with the one opposite the active card
// (distance 3, out of view) left out of the render entirely.
const capabilityDistance = (index, active, count) => {
  let distance = (((index - active) % count) + count) % count
  if (distance > count / 2) distance -= count
  return distance
}

// Drag threshold in px before a release counts as a swipe rather than a tap.
const DRAG_SWIPE_THRESHOLD = 60
// Below this, a drag is treated as a click/tap rather than a swipe, so a
// card's onClick still works for a stationary press.
const DRAG_TAP_THRESHOLD = 6

const CapabilitiesCarousel = ({ items }) => {
  const [active, setActive] = useState(0)
  const [dragOffset, setDragOffset] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const count = items.length
  const trackRef = useRef(null)
  const wasDragged = useRef(false)

  const goTo = index => setActive(((index % count) + count) % count)

  const bindDrag = useGesture(
    {
      onDrag: ({ down, movement: [mx], direction: [dx], distance }) => {
        if (distance > DRAG_TAP_THRESHOLD) wasDragged.current = true
        setIsDragging(down)
        if (down) {
          setDragOffset(mx)
        } else {
          setDragOffset(0)
          if (distance > DRAG_SWIPE_THRESHOLD) {
            goTo(active + (dx > 0 ? -1 : 1))
          }
          // Let the drag-triggered click on the card underneath the
          // pointer fire and no-op before clearing the flag.
          setTimeout(() => {
            wasDragged.current = false
          }, 0)
        }
      },
    },
    { domTarget: trackRef }
  )
  useEffect(bindDrag, [bindDrag])

  const handleCardClick = i => {
    if (wasDragged.current) return
    goTo(i)
  }

  return (
    <div className={styles.capabilitiesCarousel}>
      <div
        ref={trackRef}
        className={styles.capabilitiesTrack}
        style={{
          transform: `translateX(${dragOffset}px)`,
          transition: isDragging ? "none" : "transform 0.3s ease",
        }}
      >
        {items.map((item, i) => {
          const distance = capabilityDistance(i, active, count)
          if (Math.abs(distance) > 2) return null
          const isActive = distance === 0
          return (
            <button
              key={item.heading}
              type="button"
              className={styles.capabilityCarouselCard}
              style={{
                "--distance": distance,
                "--opacity": isActive ? 1 : Math.abs(distance) === 1 ? 0.65 : 0.38,
                "--card-scale": isActive ? 1.18 : 1,
                "--z": count - Math.abs(distance),
                // The edge cards are the only ones that can be freshly
                // mounted by a move (the card opposite active isn't
                // rendered), so they have no previous position to
                // transition from. This tells the mount animation which
                // side they come in from — without it, a card entering
                // after a swipe just rides the track's snap-back and
                // appears to slide in the opposite direction.
                "--enter-dir": Math.abs(distance) === 2 ? Math.sign(distance) : 0,
              }}
              aria-current={isActive}
              onClick={() => handleCardClick(i)}
            >
              <h3 className={`h--serif mb-2 ${styles.capabilityCardHeading}`}>
                {item.heading}
              </h3>
              <p className={styles.capabilityCardBody}>{item.body}</p>
            </button>
          )
        })}
      </div>
      <div className={styles.capabilitiesNav}>
        <button
          type="button"
          className={styles.capabilitiesArrow}
          aria-label="Previous capability"
          onClick={() => goTo(active - 1)}
        >
          ‹
        </button>
        <button
          type="button"
          className={styles.capabilitiesArrow}
          aria-label="Next capability"
          onClick={() => goTo(active + 1)}
        >
          ›
        </button>
      </div>
    </div>
  )
}

const FaqItem = ({ item, isOpen, onToggle }) => (
  <div className={styles.faqItem}>
    <button
      type="button"
      className={styles.faqQuestion}
      aria-expanded={isOpen}
      onClick={onToggle}
    >
      <span className={`h--serif ${styles.faqQuestionText}`}>{item.question}</span>
      <span className={styles.faqIndicator}>{isOpen ? "–" : "+"}</span>
    </button>
    {isOpen && (
      <div className={styles.faqAnswer}>
        <p>{item.answer}</p>
      </div>
    )}
  </div>
)

// The shared zDarkTheme (from hooks/useTheme, via components/layout) reads
// its colors off _settings.scss's :export block at module-load time. On
// this page specifically that block resolves empty at build time (a
// pre-existing SSR/webpack quirk unrelated to this page's own styles —
// confirmed by reproducing it with the stylesheet import removed entirely),
// which silently drops the header logo/nav to black-on-black. Hardcoding
// the same values here sidesteps it.
const pageTheme = {
  foreground: "#FCFAF3", // $cream
  background: "#1A1C2C", // $midnight
  scheme: "dark-on-light",
  bodyClass: "zeus",
}

const LivingBrandStudio = ({ data }) => {
  const page = data.craft.page
  const seo = page.seomatic
  const [openFaq, setOpenFaq] = useState(0)

  return (
    <Layout seo={seo} theme={pageTheme}>
      {/* 1. Hero */}
      <div className={styles.hero}>
        <div className="wrapper">
          <HeroHeading />
        </div>
      </div>

      {/* 2. Philosophy */}
      <div className={`${styles.section} ${styles.philosophy}`}>
        <div className="wrapper">
          <div className={`${styles.pill} subhead subhead--small fw-bold ${styles.philosophyEyebrow}`}>
            AI-enabled Living Brands
          </div>
          <h2 className={`h-display h--serif mb-4 ${styles.philosophyHeading}`}>
            Living Brands are built to think.
          </h2>
          <p className={`body-large-after-tablet ${styles.philosophyBody}`}>
            They hold onto the values, truths and hard-won expertise that give
            your brand continuity, while adapting to changes in culture and
            category.
          </p>
          <p className={`body-after-tablet mb-5 ${styles.philosophySecondary}`}>
            The result is a generative point of view, rooted in what matters
            and responsive to the world around it.
          </p>
          <a className={styles.ctaDark} href="/contact">
            Talk to us about your brand
          </a>
        </div>
      </div>

      {/* 3. Compare */}
      <div className={`${styles.section} ${styles.compare}`}>
        <div className="wrapper">
          <div className={`subhead subhead--small fw-bold ${styles.eyebrow}`}>
            The category
          </div>
          <h2 className={`h-1 h--serif ${styles.heading52}`}>
            What makes a Living Brand different?
          </h2>
          <div className={styles.compareGrid}>
            <div className={styles.compareColumn}>
              <div className={`subhead subhead--small fw-bold ${styles.eyebrow}`}>
                Conventional brands
              </div>
              <h3 className={`h-2 h--serif mb-3 ${styles.compareSubhead}`}>
                Made to be managed.
              </h3>
              <p className="body-large-after-tablet">
                Conventional brands place a premium on consistency, preserving
                rules and guidelines. But new markets, products, channels and
                cultural moments, present challenges that these static systems
                cannot anticipate.
              </p>
            </div>
            <div className={styles.compareColumn}>
              <div className={`subhead subhead--small fw-bold ${styles.eyebrow}`}>
                Living brands
              </div>
              <h3 className={`h-2 h--serif mb-3 ${styles.compareSubhead}`}>
                Made to be taught.
              </h3>
              <p className="body-large-after-tablet">
                Living Brands learn from the choices, lessons and context that
                shape your work. This accumulated knowledge becomes part of
                the system, giving teams a stronger, more attuned foundation
                for future decisions.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 4. Quote — animated gradient background from the Figma export */}
      <div className={`${styles.section} ${styles.quote}`}>
        <video
          className={styles.quoteVideo}
          src="/videos/living-brand-studio/quote-gradient.mp4"
          autoPlay
          loop
          muted
          playsInline
        />
        <div className={`wrapper ${styles.quoteContent}`}>
          <p className={`${styles.quoteText} h-1 h--serif`}>
            Zeus Jones pioneered the Living Brand model: A human-centered,
            AI-amplified brand system that learns, adapts, and scales in real
            time.
          </p>
        </div>
      </div>

      {/* 5. People — updated wireframe: plain midnight background (no gradient) */}
      <div className={`${styles.section} ${styles.people}`}>
        <div className="wrapper">
          <div className={`subhead subhead--small fw-bold ${styles.eyebrow} ${styles.peopleEyebrow}`}>
            Human at the heart
          </div>
          <h2 className={`h-1 h--serif mb-4 ${styles.peopleHeading}`}>
            Living Brands are built around people.
          </h2>
          <div className={`${styles.peopleBody} body-large-after-tablet`}>
            <p>
              Most brand intelligence tools are file libraries designed to
              shortcut copy and design. <strong className="fw-bold">We start with your people
              instead.</strong> With their judgment, instincts, and taste. The
              things no one ever writes down, the things scattered across
              Slack threads, conversations, and long-tenured staff. The things
              that rarely, if ever, make it into a brand guide.
            </p>
            <p>
              Living Brands make the knowledge and experience of your best
              people accessible to every team, every tool, and every
              decision.
            </p>
          </div>
          <div className={styles.peopleCards}>
            <div className={styles.peopleCard}>
              <video
                className={styles.peopleCardVideo}
                src="/videos/living-brand-studio/listen-encore-govern.mp4"
                autoPlay
                loop
                muted
                playsInline
              />
              <div className={styles.peopleCardContent}>
                <span className={`subhead subhead--small fw-bold ${styles.peopleCardLabel}`}>
                  01 · Listen
                </span>
                <h3 className={`h-5 h--serif mb-2 ${styles.peopleCardHeading}`}>
                  In-depth interviews with your teams
                </h3>
                <p className={styles.peopleCardBody}>
                  We surface and capture knowledge that lives in the minds of
                  your best people—not just in documents.
                </p>
              </div>
            </div>
            <div className={styles.peopleCard}>
              <video
                className={styles.peopleCardVideo}
                src="/videos/living-brand-studio/listen-encore-govern.mp4"
                autoPlay
                loop
                muted
                playsInline
              />
              <div className={styles.peopleCardContent}>
                <span className={`subhead subhead--small fw-bold ${styles.peopleCardLabel}`}>
                  02 · Encode
                </span>
                <h3 className={`h-5 h--serif mb-2 ${styles.peopleCardHeading}`}>
                  Structured by brand experts, not automation
                </h3>
                <p className={styles.peopleCardBody}>
                  We architect Living Brands using your actual decision logic,
                  behavior rules, and hierarchies to build a system that
                  reasons, rather than recites.
                </p>
              </div>
            </div>
            <div className={styles.peopleCard}>
              <video
                className={styles.peopleCardVideo}
                src="/videos/living-brand-studio/listen-encore-govern.mp4"
                autoPlay
                loop
                muted
                playsInline
              />
              <div className={styles.peopleCardContent}>
                <span className={`subhead subhead--small fw-bold ${styles.peopleCardLabel}`}>
                  03 · Govern
                </span>
                <h3 className={`h-5 h--serif mb-2 ${styles.peopleCardHeading}`}>
                  Governed to evolve without drifting
                </h3>
                <p className={styles.peopleCardBody}>
                  Built-in oversight and calibration tools keep the system on
                  course. It learns. It adapts. It doesn't wander.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 6. Studio */}
      <div className={`${styles.section} ${styles.studio}`}>
        <div className="wrapper">
          <div className={`subhead subhead--small fw-bold ${styles.eyebrow} ${styles.studioEyebrow}`}>
            The system
          </div>
          <div className={styles.studioCard}>
            <div className={styles.studioIntro}>
              <h2 className={`h-1 h--serif mb-4 ${styles.studioMainHeading}`}>
                The Living Brand Studio
              </h2>
              <p>
                This is the nerve center of the Living Brand: a central
                workspace that applies dynamic, AI-enabled tools to your
                everyday workflows.
              </p>
              <ul className={styles.studioBullets}>
                <li>
                  Gives teams an always-on Brand Steward for questions,
                  recommendations, and rationale.
                </li>
                <li>
                  Offers ready-made and custom apps for high-frequency
                  workflows like briefs, campaign development, and
                  onboarding.
                </li>
                <li>
                  Builds Connected Workflows to bring the Living Brand into
                  existing tools and processes.
                </li>
              </ul>
            </div>
            <div className={styles.studioPanels}>
              <div className={styles.studioPanel}>
                <h3 className={`h-3 h--serif mb-3 ${styles.studioPanelHeading}`}>
                  Intelligence
                </h3>
                <div className={styles.studioPanelIcon}>
                  <IntelligenceIcon />
                </div>
                <p className={styles.studioPanelBody}>
                  This layer encodes the principles, instincts, behaviors, and
                  decision logic that make your brand distinct.
                </p>
                <ul className={styles.studioPanelBullets}>
                  <li>
                    Built from real expertise, deep interviews, documentation
                    analysis, and brand architecture
                  </li>
                  <li>
                    Structured to connect with every AI workflow and tool
                    your teams already use
                  </li>
                  <li>
                    Governed to stay coherent over time through
                    human-reviewed updates and stewardship.
                  </li>
                </ul>
              </div>
              <div className={styles.studioPanel}>
                <h3 className={`h-3 h--serif mb-3 ${styles.studioPanelHeading}`}>
                  Sensing
                </h3>
                <div className={styles.studioPanelIcon}>
                  <SensingIcon />
                </div>
                <p className={styles.studioPanelBody}>
                  This layer keeps the system current. It helps the brand
                  respond to what matters, without chasing every shift in the
                  wind.
                </p>
                <ul className={styles.studioPanelBullets}>
                  <li>
                    Attunes to culture, consumers, competitors, and
                    performance.
                  </li>
                  <li>
                    Spots drift before it compounds. Calibrates tone,
                    emotional accuracy, and risk in real time.
                  </li>
                  <li>Feeds what it learns back into the system, so it gets sharper with use.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 7. Capabilities */}
      <div className={`${styles.section} ${styles.capabilities}`}>
        <div className="wrapper">
          <div className={`subhead subhead--small fw-bold ${styles.eyebrow}`}>
            The model in action
          </div>
          <h2 className={`h-1 h--serif ${styles.heading52}`}>
            What can you do in the Living Brand Studio?
          </h2>
          <CapabilitiesCarousel items={CAPABILITIES} />
        </div>
      </div>

      {/* 8. Process */}
      <div className={`${styles.section} ${styles.process}`}>
        <div className="wrapper">
          <h2 className={`h-1 h--serif mb-4 ${styles.heading52}`}>
            How does it work?
          </h2>
          <p className={styles.processIntro}>
            Living Brand projects can start with a focused prototype, a
            deeper system build, or the activation of specific tools and
            workflows. We shape each engagement around your brand, your
            opportunities, and the work you need the system to support.
          </p>
          <div className={styles.processGrid}>
            {PROCESS_STEPS.map(step => (
              <div key={step.name} className={styles.processStep}>
                {/* TODO(assets): swatch is a flat color for now — real icon coming later */}
                <div
                  className={styles.processStepSwatch}
                  style={{ backgroundColor: step.swatch }}
                />
                <div className={styles.processStepBody}>
                  <h3 className={`h-5 h--serif mb-2 ${styles.processStepHeading}`}>
                    {step.name}
                  </h3>
                  <p className={styles.processStepBodyText}>{step.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 9. Practice — confirmed back in the latest wireframe, re-enabled */}
      {SHOW_PRACTICE && (
        <div className={`${styles.section} ${styles.practice}`}>
          <div className="wrapper">
            <div className={`subhead subhead--small fw-bold ${styles.eyebrow}`}>
              Work in progress
            </div>
            <h2 className="h-1 h--serif">Living Brands in practice</h2>
            <div className={styles.practiceGrid}>
              {PRACTICE.map(item => (
                <div key={item.heading} className={styles.practiceCard}>
                  {/* TODO(assets): latest wireframe still shows a gradient tile here rather than a
                     photo — confirm whether this is the intended final look or a photo is still coming */}
                  <div className={styles.practiceCardImage}>
                    <span className={`${styles.practiceCardTag} subhead subhead--small fw-bold`}>
                      {item.tag}
                    </span>
                  </div>
                  <h3 className="h-5 h--serif mb-2">{item.heading}</h3>
                  <p className={`body-large-after-tablet ${styles.practiceCardBody}`}>
                    {item.body}
                  </p>
                  <p className="body-large-after-tablet fw-bold">{item.callout}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* 10. FAQ */}
      <div className={`${styles.section} ${styles.faq}`}>
        <div className="wrapper">
          <div className={`subhead subhead--small fw-bold ${styles.eyebrow}`}>
            Questions
          </div>
          <h2 className={`h-1 h--serif ${styles.heading52}`}>
            Frequently asked questions
          </h2>
          <div className={styles.faqList}>
            {FAQS.map((item, i) => (
              <FaqItem
                key={item.question}
                item={item}
                isOpen={openFaq === i}
                onToggle={() => setOpenFaq(openFaq === i ? null : i)}
              />
            ))}
          </div>
        </div>
      </div>

      {/* 11. Inside — no image, matches the latest wireframe's single-column layout */}
      <div className={`${styles.section} ${styles.inside}`}>
        <div className="wrapper">
          <div className={`subhead subhead--small fw-bold ${styles.eyebrow}`}>
            Go deeper
          </div>
          <h2 className="h-display h--serif mb-3">Inside Living Brands</h2>
          <p className={`body-large-after-tablet ${styles.insideBody}`}>
            Read more about the emerging ideas that shape our approach to
            Living Brands, from human judgment and AI to coherence,
            creativity, and change.
          </p>
          <a className={styles.ctaDark} href="/athena">
            Enter the rabbit hole
          </a>
        </div>
      </div>

      {/* 12. Closing — animated gradient background from the Figma export */}
      <div className={`${styles.section} ${styles.closing}`}>
        <video
          className={styles.closingVideo}
          src="/videos/living-brand-studio/closing-gradient.mp4"
          autoPlay
          loop
          muted
          playsInline
        />
        <div className={`wrapper ${styles.closingContent}`}>
          <div className={`subhead subhead--small fw-bold ${styles.eyebrow} ${styles.closingEyebrow}`}>
            Start here
          </div>
          <h2 className={`h-1 h--serif ${styles.closingHeading}`}>
            Zeus Jones can turn brand truths, cultural signals, and human
            expertise into a Living Brand system your team will use every
            day. <em className="h--serif-italic">Let's build yours.</em>
          </h2>
          <div className={styles.closingLinks}>
            <a className={styles.ctaLight} href="/contact">
              Talk to us
            </a>
          </div>
        </div>
      </div>
    </Layout>
  )
}

export default LivingBrandStudio

export const query = graphql`
  query {
    craft {
      page: entry(section: "livingBrandStudio") {
        title
        seomatic {
          metaTagContainer
          metaLinkContainer
          metaTitleContainer
          metaJsonLdContainer
        }
      }
    }
  }
`
